// iter 379h — Vehicle invoice login popup.
// Jake: emailed vehicle invoices auto-parse; "if there's any questions
// just have a pop up next time I log in." Shows once per session when
// there are parses needing review or unacknowledged auto-commits.
import { useEffect, useState } from "react";
import { hasAnyRole } from "../lib/roles";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VehicleInvoicePopup = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasAnyRole(["admin", "dispatcher", "manager"])) return;
    if (sessionStorage.getItem("vi_popup_shown") === "1") return;
    axios.get(`${API}/vehicle-invoices/pending`)
      .then((res) => {
        const list = res.data?.items || [];
        if (list.length > 0) {
          setItems(list);
          setOpen(true);
          sessionStorage.setItem("vi_popup_shown", "1");
        }
      })
      .catch(() => {});
  }, []);

  if (!open || items.length === 0) return null;

  const ack = (id) => {
    axios.post(`${API}/vehicle-invoices/${id}/ack`).catch(() => {});
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const dismiss = (id) => {
    axios.post(`${API}/vehicle-invoices/${id}/dismiss`).catch(() => {});
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const review = (id) => {
    setOpen(false);
    navigate(`/stock-overview?invoice_review=${id}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg bg-[#111113] border-white/10 text-white" data-testid="vehicle-invoice-popup">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileText className="h-4 w-4 text-zinc-400" />
            Vehicle Invoices
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {items.map((it) => (
            <div
              key={it.id}
              className="rounded-lg border border-white/10 bg-white/[0.03] p-3"
              data-testid={`vehicle-invoice-item-${it.invoice_number || it.id}`}
            >
              <div className="flex items-start gap-2">
                {it.status === "auto_committed" ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">
                    {it.invoice_number ? `Invoice ${it.invoice_number}` : it.subject}
                    <span className="text-zinc-500 font-normal ml-1.5">· {it.manufacturer || ""}</span>
                  </div>
                  {it.status === "auto_committed" ? (
                    <div className="text-xs text-emerald-300/90 mt-0.5">
                      Auto-parsed — {it.commit_result?.mode === "backfill"
                        ? `${it.commit_result?.carts_backfilled ?? 0} VIN(s) backfilled onto the existing order`
                        : `${it.commit_result?.carts_created ?? 0} cart(s) added as Ordered`}
                      {(it.commit_result?.skipped_existing_vins?.length || 0) > 0 &&
                        `, ${it.commit_result.skipped_existing_vins.length} duplicate VIN(s) skipped`}
                    </div>
                  ) : (
                    <ul className="text-xs text-amber-200/90 mt-0.5 list-disc pl-4 space-y-0.5">
                      {(it.questions || []).slice(0, 4).map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-2">
                {it.status === "auto_committed" ? (
                  <Button size="sm" variant="outline" className="h-7 text-xs border-white/15 bg-transparent text-zinc-200 hover:bg-white/10"
                    onClick={() => ack(it.id)} data-testid={`vi-ack-${it.id}`}>
                    OK
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-white/15 bg-transparent text-zinc-400 hover:bg-white/10"
                      onClick={() => dismiss(it.id)} data-testid={`vi-dismiss-${it.id}`}>
                      Dismiss
                    </Button>
                    <Button size="sm" className="h-7 text-xs bg-[#007AFF] hover:bg-[#0A84FF] text-white"
                      onClick={() => review(it.id)} data-testid={`vi-review-${it.id}`}>
                      Review &amp; Commit
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
